import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  adminGuard,
  JwtGuard,
  MobileInaccessibleGuard,
  mfaRequiredGuard,
  profilePermissionsGuard,
} from '@et/guards';
import { ProfileResolver } from '@et/resolvers';
import { ETPermissions } from '@et/typings';
import { HomeComponent } from './home/home.component';
import { landingGuard } from './landing/landing.guard';
import { ejournalSessionStateProvider } from '@et/presentational-et-journal';

const routes: Routes = [
  {
    path: '',
    children: [
      {
        path: 'app',
        component: HomeComponent,
        /**
         * `JwtGuard` now branches internally on `environment.useBetterAuth` and handles both
         * the OIDC and MSAL session checks. Dropping `MsalGuard` here removes the implicit
         * MSAL interactive-login trigger; unauthenticated users land on `/auth/login`
         * regardless of which backend is active.
         */
        canActivate: [JwtGuard, mfaRequiredGuard],
        resolve: { profile: ProfileResolver },
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [landingGuard],
            children: [],
          },
          {
            path: 'doc-list',
            canActivate: [MobileInaccessibleGuard, profilePermissionsGuard],
            providers: [ejournalSessionStateProvider],
            data: {
              permissions: {
                only: [
                  ETPermissions.NotarySigningAgent,
                  ETPermissions.NotaryNotary,
                  ETPermissions.TitleSigningAgent,
                  ETPermissions.Title,
                  ETPermissions.ClosingCompany,
                  ETPermissions.Signor,
                ],
                redirectTo: '/app/profile',
              },
            },
            loadChildren: () =>
              import('@et/doc-list').then((mod) => mod.FeatureDocListModule),
          },
          {
            path: 'doc-upload',
            canActivate: [MobileInaccessibleGuard, profilePermissionsGuard],
            data: {
              permissions: {
                only: [
                  ETPermissions.NotarySigningAgent,
                  ETPermissions.NotaryNotary,
                  ETPermissions.TitleSigningAgent,
                  ETPermissions.Title,
                  ETPermissions.ClosingCompany,
                  ETPermissions.Signor,
                ],
                redirectTo: '/app/profile',
              },
            },
            loadChildren: () =>
              import('@et/doc-upload').then(
                (mod) => mod.FeatureDocUploadModule,
              ),
          },
          {
            path: 'orders',
            canActivate: [MobileInaccessibleGuard, profilePermissionsGuard],
            data: {
              permissions: {
                only: [
                  ETPermissions.LenderClosingDepartment,
                  ETPermissions.LenderLoanOfficer,
                ],
                redirectTo: '/app/doc-list',
              },
            },
            loadChildren: () =>
              import('@et/orders').then((mod) => mod.FeatureOrdersModule),
          },
          {
            path: 'profile',
            canActivate: [MobileInaccessibleGuard],
            loadChildren: () =>
              import('@et/profile').then((mod) => mod.FeatureProfileModule),
          },
          {
            path: 'signing',
            loadChildren: () =>
              import('@et/signing').then((mod) => mod.FeatureSigningModule),
          },
          {
            path: 'admin',
            canActivate: [adminGuard],
            loadChildren: () =>
              import('@et/web-admin').then((mod) => mod.adminRoutes),
          },
        ],
      },
      {
        path: 'link',
        loadChildren: () =>
          import('@et/secure-links').then(
            (mod) => mod.FeatureSecureLinksModule,
          ),
      },
      {
        path: 'auth',
        loadChildren: () => import('@et/auth').then((mod) => mod.AuthModule),
      },
      {
        path: 'error',
        loadChildren: () =>
          import('@et/error-pages').then((mod) => mod.ErrorPagesModule),
      },
      { path: '', redirectTo: 'app', pathMatch: 'full' },
    ],
  },
  { path: '**', redirectTo: '', pathMatch: 'full' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { bindToComponentInputs: true })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
